/**
 * WordPress dependencies
 */
import domReady from '@wordpress/dom-ready';

/**
 * Internal dependencies
 */
import { getAlternative } from './alternative';
import { doesVisitorParticipate } from '../utils/participation';
import { updateSegmentationSettings } from '../utils/segments';
import { doSingleAction } from '../utils/actions';
import {
	addQueryArgs,
	getQueryArgs,
	isLocalUrl,
	removeArgsAndHash,
	removeQueryArgs,
} from '../utils/url';

export function showPage( settings, targetUrl ) {
	const { isTestedPostRequest } = settings;
	if ( isTestedPostRequest ) {
		try {
			window.history.replaceState( '', '', targetUrl );
		} catch ( e ) {} //end try
		validateCurrentPage( settings );
		return;
	} //end if

	const { ignoreTrailingSlash, currentUrl } = settings;
	if ( areUrlsEqual( ignoreTrailingSlash, targetUrl, currentUrl ) ) {
		validateCurrentPage( settings );
	} else if ( isAlternativeRequestNoLongerValid( settings ) ) {
		stayWithNoTestingArgs();
		validateCurrentPage( settings );
	} else {
		redirect( settings, targetUrl );
	} //end if
} //end showPage()

function validateCurrentPage( settings ) {
	if ( doesVisitorParticipate( settings ) ) {
		updateSegmentationSettings( settings, 'future' );
		preloadQueryArgs( settings );
	} //end if
	cleanTestingQueryArgs( settings );
	doSingleAction( 'valid-content' );
} //end validateCurrentPage()

function redirect( settings, targetUrl ) {
	const { referrerParam } = settings;
	targetUrl = addMissingArguments( targetUrl, referrerParam );
	targetUrl = maybeAddHash( targetUrl );

	if ( ! document.location.replace ) {
		document.location.href = targetUrl;
		return;
	} //end if
	document.location.replace( targetUrl );
} //end redirect()

function stayWithNoTestingArgs() {
	const url = removeQueryArgs(
		document.location.href,
		'nab',
		'nabforce',
		'nabstaging',
		'nab_experiments_with_page_view'
	);
	try {
		window.history.replaceState( '', '', url );
	} catch ( e ) {} //end try
} //end stayWithNoTestingArgs()

function getReferrer( referrerParam ) {
	const query = document.location.search.replace( /^\?/, '' );
	const params = query.split( '&' ).reduce( ( memo, param ) => {
		const [ name, ...value ] = param.split( '=' );
		memo[ name ] = value.join( '=' );
		return memo;
	}, {} );

	if ( params[ referrerParam ] ) {
		return decodeURIComponent( params[ referrerParam ] );
	} //end if

	const referrer = document.referrer;
	const currentUrl = document.location.href;
	if ( isSameDomain( currentUrl, referrer ) ) {
		return false;
	} //end if

	return referrer;
} //end getReferrer()

function isSameDomain( oneUrl = '', anotherUrl = '' ) {
	const clean = ( x ) =>
		x.replace( /^https?:\/\//, '' ).replace( /\/.*$/, '' );
	return clean( oneUrl ) === clean( anotherUrl );
} //end isSameDomain()

function cleanTestingQueryArgs( settings ) {
	let url = document.location.href;
	url = removeQueryArgs(
		url,
		'nabforce',
		'nabstaging',
		'nab_experiments_with_page_view'
	);

	if ( settings.hideQueryArgs ) {
		url = removeQueryArgs( url, 'nab' );
	} //end if

	try {
		window.history.replaceState( '', '', url );
	} catch ( e ) {} //end try
} //end cleanTestingQueryArgs()

function areUrlsEqual(
	ignoreTrailingSlash = false,
	oneUrl = '',
	anotherUrl = ''
) {
	let oneCleanUrl = removeArgsAndHash( oneUrl );
	let anotherCleanUrl = removeArgsAndHash( anotherUrl );

	if ( ignoreTrailingSlash ) {
		oneCleanUrl = `${ oneCleanUrl }/`.replace( /\/\/$/, '/' );
		anotherCleanUrl = `${ anotherCleanUrl }/`.replace( /\/\/$/, '/' );
	} //end if

	if ( oneCleanUrl !== anotherCleanUrl ) {
		return false;
	} //end if

	const oneArgs = getQueryArgs( oneUrl );
	const anotherArgs = getQueryArgs( anotherUrl );

	const oneArgKeys = Object.keys( oneArgs );
	const anotherArgKeys = Object.keys( anotherArgs );
	if ( oneArgKeys.length !== anotherArgKeys.length ) {
		return false;
	} //end if

	for ( const key of oneArgKeys ) {
		if ( ! anotherArgKeys.includes( key ) ) {
			return false;
		} //end if
	} //end for

	for ( const key of oneArgKeys ) {
		if ( oneArgs[ key ] !== anotherArgs[ key ] ) {
			return false;
		} //end if
	} //end if

	return true;
} //end areUrlsEqual()

function addMissingArguments( url, referrerParam ) {
	const expectedArguments = getQueryArgs( url );
	const currentArguments = getQueryArgs( document.location.search );

	delete currentArguments.nab;
	delete currentArguments.nabforce;
	delete currentArguments.nabstaging;

	url = addQueryArgs( url, {
		...currentArguments,
		...expectedArguments,
	} );

	if ( ! referrerParam ) {
		return url;
	} //end if

	let referrer = getReferrer( referrerParam );
	if ( ! referrer ) {
		return url;
	} //end if

	referrer = removeQueryArgs( referrer, referrerParam );
	return addQueryArgs( url, { [ referrerParam ]: referrer } );
} //end addMissingArguments()

function maybeAddHash( url ) {
	if ( -1 !== url.indexOf( '#' ) ) {
		return url;
	} //end if

	return url + document.location.hash;
} //end maybeAddHash()

function isAlternativeRequestNoLongerValid( settings ) {
	const currentArguments = getQueryArgs( document.location.search );
	const isAlternativeRequest = Object.keys( currentArguments ).includes(
		'nab'
	);
	return 0 === settings.numOfAlternatives && isAlternativeRequest;
} //end isAlternativeRequestNoLongerValid()

let preloaded = false;
function preloadQueryArgs( settings ) {
	if ( ! settings.preloadQueryArgs || preloaded ) {
		return;
	} //end if
	preloaded = true;

	const nab = getAlternative( settings ) % settings.preloadQueryArgs;
	domReady( () =>
		[ ...document.querySelectorAll( 'a' ) ]
			.filter( ( a ) => isLocalUrl( a.href ) )
			.filter( ( a ) => ! a.href.includes( '/wp-content/' ) )
			.filter( ( a ) => ! a.getAttribute( 'href' ).startsWith( '#' ) )
			.forEach( ( a ) => {
				a.href = addQueryArgs( a.href, { nab } );
			} )
	);
} //end preloadQueryArgs()
